import { Form } from 'antd';
import Box from 'components/Box';
import Modal from 'components/Modal';
import dayjs from 'dayjs';
import appointmentApis from 'features/appointment/services/apis';
import { getTimeSections } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2/helpers';
import { ModalDurations, useModalDurationsRef } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2/TimeDate/ModalDurations';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import styled from 'styled-components';

type IModalEditStartTimeProps = {};
type IModalStartTimeResponse = {
  startTime: string;
  duration: number;
};
type IOpenConfigs = {
  staffId: string;
  date: string;
  serviceName?: string;
  startTime?: string;
  duration?: number;
};
type IModalEditStartTimeRef = {
  open: (configs: IOpenConfigs) => Promise<IModalStartTimeResponse | undefined>;
  close: () => void;
};

const durations = [15, 30, 45, 60, 75, 90, 105, 120];

export const ModalEditStartTimeRef = React.createRef<IModalEditStartTimeRef>();
const ModalEditStartTime = forwardRef<IModalEditStartTimeRef, IModalEditStartTimeProps>((props, ref) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeslot, setTimeslot] = useState<string[]>([]);
  const [serviceName, setServiceName] = useState('');
  const [form] = Form.useForm();
  const callbackResolve = useRef<(res?: IModalStartTimeResponse) => void>(() => undefined);

  const handleOpen: IModalEditStartTimeRef['open'] = (configs) => {
    form.resetFields();
    form.setFieldsValue({ startTime: configs.startTime, duration: configs.duration });
    setServiceName(configs.serviceName || 'Edit Start Time');
    setTimeslot([]);
    setOpen(true);
    fetchStartTime(configs.staffId, configs.date);
    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose: IModalEditStartTimeRef['close'] = () => {
    setOpen(false);
    callbackResolve.current(undefined);
  };

  const fetchStartTime = async (staffId: string, date: string) => {
    setLoading(true);
    try {
      const resData: IResponseDataBody<string[]> = await appointmentApis.getStaffTimeSlot(staffId || '', date || '');
      const list = resData.data.data;
      setTimeslot(list ? list.map((d) => dayjs(d, 'HH:mm').format('hh:mm A')) : []);
    } catch (error) {
      setTimeslot([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values: any) => {
    callbackResolve.current(values as IModalStartTimeResponse);
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const sections = getTimeSections(timeslot);

  return (
    <Modal
      noneBodyStyle
      width={1200}
      visible={open}
      onSubmit={() => form.submit()}
      modalTitle={serviceName}
      v2
      onClose={handleClose}
    >
      <Container form={form} onFinish={handleSubmit}>
        <ContainerLeftBox>
          <div className="section-label">BEGIN TIME SERVICES:</div>
          {loading && <div className="section-sublabel">Loading...</div>}
          {!loading && !timeslot.length && <div className="section-sublabel">No available time</div>}
          {sections.morning.length > 0 && (
            <div className="time-slot-section">
              <div className="section-sublabel">Morning</div>
              <Form.Item noStyle name="startTime">
                <StartTimeChange timeslots={sections.morning} />
              </Form.Item>
            </div>
          )}
          {sections.afternoon.length > 0 && (
            <div className="time-slot-section">
              <div className="section-sublabel">Afternoon</div>
              <Form.Item noStyle name="startTime">
                <StartTimeChange timeslots={sections.afternoon} />
              </Form.Item>
            </div>
          )}
          {sections.evening.length > 0 && (
            <div className="time-slot-section">
              <div className="section-sublabel">Evening</div>
              <Form.Item noStyle name="startTime">
                <StartTimeChange timeslots={sections.evening} />
              </Form.Item>
            </div>
          )}
        </ContainerLeftBox>
        <ContainerRightBox>
          <div className="section-label">CHANGE THE SERVICE TIME:</div>
          <Form.Item name="duration" noStyle>
            <Durations />
          </Form.Item>
        </ContainerRightBox>
      </Container>
    </Modal>
  );
});

const StartTimeChange = ({ value, onChange, timeslots = [] }: { value?: string; onChange?: (value?: string) => void; timeslots?: string[] }) => {
  return (
    <ContainerTimeSlot>
      {timeslots.map((o) => (
        <button type="button" onClick={() => onChange?.(o)} key={o} className={`start-time-item ${value === o ? 'active' : ''}`}>
          <span>{o}</span>
        </button>
      ))}
    </ContainerTimeSlot>
  );
};

const Durations = ({ value, onChange }: { value?: number; onChange?: (value?: number) => void }) => {
  const modalRef = useModalDurationsRef();
  return (
    <ContainerTimeSlot className="durations">
      {durations.map((o) => (
        <button type="button" onClick={() => onChange?.(o)} key={o} className={`start-time-item ${value === o ? 'active' : ''}`}>
          <span>{o}m</span>
        </button>
      ))}
      <button type="button" onClick={() => modalRef.current?.open()} className={`start-time-item ${!durations.includes(value || 0) ? 'active' : ''}`}>
        <span>Custom {(!!value && !durations.includes(value || 0)) ? ` (${value || 0}m)` : ''}</span>
      </button>
      <ModalDurations ref={modalRef} onSubmit={(o) => onChange?.(o)} />
    </ContainerTimeSlot>
  );
};

ModalEditStartTime.displayName = 'ModalEditStartTime';

export default ModalEditStartTime;

const ContainerLeftBox = styled(Box)`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
  background: #fff;
  box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15);
`;
const ContainerRightBox = styled(Box)`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  width: 350px;
`;
const ContainerTimeSlot = styled.div`
  align-self: stretch;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 8px;
  .start-time-item {
    display: flex;
    padding: 16px 8px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 5px;
    border: 1px solid #ccd4dc;
    background: #fff;
    cursor: pointer;
    span {
      color: #1d2129;
      text-align: center;
      font-family: Poppins;
      font-size: 22px;
      font-weight: 400;
      line-height: normal;
    }
    &.active {
      border: 1px solid #2d6772;
      background: #2d6772;
      span {
        color: #fff;
      }
    }
  }
  &.durations {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    .start-time-item {
      align-self: stretch;
    }
  }
`;
const Container = styled(Form)`
  display: flex;
  gap: 8px;
  .section-label {
    color: #505050;
    font-family: Poppins;
    font-size: 22px;
    font-weight: 600;
    line-height: normal;
  }
  .time-slot-section {
    display: flex;
    padding: 8px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    background: #f6f7fc;
    margin-bottom: 8px;
  }
  .section-sublabel {
    color: #505050;
    text-align: left;
    font-family: Poppins;
    font-size: 20px;
    font-weight: 600;
    line-height: normal;
    margin-top: 8px;
    margin-bottom: 8px;
  }
`;
